import React, {useCallback, useMemo, useState} from "react";
import {Card} from "@mui/material";
import NewDropZone from "../../../../../components/base/new-drop-zone";
import {InputFileAcceptMimes, InputFileAcceptTypes} from "../../../../../../core/constants/enums";
import classnames from "classnames";
import LazyImage from "../../../../../components/app-specific/lazy-image";
import {ReactComponent as DefaultUserImage} from "../../../../../../assets/images/operator-profile-placeholder/operator-profile-edit-image-placeholder.svg";
import Button from "../../../../../components/base/button/button";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import DateUtils from "../../../../../../core/services/date-utils";
import {useOperatorInformation, useOperatorInformationController} from "../../../../../hooks/use-operator-information";
import Grid2 from "@mui/material/Unstable_Grid2";
import CacheService from "../../../../../../core/services/cache/cache_service";


const EditOperatorProfileImageSection = () => {
    const operatorInformation = useOperatorInformation();
    const {setCachedOperatorInformation} = useOperatorInformationController()
    const [tempImage, setTempImage] = useState([null, null]);
    const [submitting, setSubmitting] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Memo version of imageUrl
     * @type {string}
     */
    const imageUrl = useMemo(() => {
        const image = CacheService.getOperatorProfileImage(operatorInformation?.id);
        if (image) {
            return image.concat(`?${DateUtils.serverNow().valueOf()}`);
        }
        return null;
    }, [operatorInformation]);

    /**
     * Sets the temporary image file for uploading purposes
     * @param {any[]} files
     */
    const setImageFile = useCallback((files) => {
        setCanSubmit(true);
        setTempImage(prevState => [prevState[0], files[0]]);
    }, []);

    /**
     * Sets the temporary image for the preview purposes.
     * @param {any[]} parsedImages
     */
    const setParsedImage = useCallback((parsedImages) => {
        setTempImage(prevState => [parsedImages[0], prevState[1]]);
    }, []);

    /**
     * Updates the profile image of the user form the server and forces sidebar profile component update
     */
    const updateProfileImageOfUser = useCallback(async () => {
        setSubmitting(true);
        const extension = tempImage[0].toString().split(';')[0].split('/')[1];
        const response = await Api.updateOperator({
            ...operatorInformation,
            HasProfileChanged: true,
            ProfileImage: tempImage[1],
            ProfileImageFormat: extension,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCachedOperatorInformation(prevState => ({
                ...prevState,
                profileImage: prevState.profileImage,
                profileImageFormat: extension,
            }));
        }
        setSubmitting(false);
        setTempImage([null, null]);
        setCanSubmit(false);
    }, [isMounted, operatorInformation, setCachedOperatorInformation, tempImage]);


    return (
        <>
            <Card variant={'outlined'} sx={{padding: 3}}>
                <Grid2 container spacing={4}>
                    <Grid2 xs={12}>
                        <p className={'text-lg font-weight-semi-bold text-color-1'}>
                            Edit Profile Picture
                        </p>
                    </Grid2>
                    <Grid2 xs={12} className={'d-flex align-items-center justify-content-center operator-profile-image'}>
                        <NewDropZone
                            setFiles={setImageFile}
                            setParsedImages={setParsedImage}
                            loading={false}
                            uploading={submitting}
                            openOnClick={true}
                            accept={[
                                InputFileAcceptMimes.images,
                                InputFileAcceptTypes.heic,
                                InputFileAcceptTypes.heif,
                            ]}
                            multiple={false}
                            id={'profile-picture'}
                            notAcceptedDurationInMs={2000}
                            endDragging={() => {
                            }}
                        >
                            {(_, highlight, notAccepted) => (
                                <>
                                    <div className={classnames('current-operator-image')}>
                                        {
                                            submitting
                                                ? <div className={'loading operator-profile-image-uploader'}>
                                                    <div/>
                                                </div>
                                                : (!(!!(tempImage[0] ?? imageUrl)))
                                                    ? <DefaultUserImage className={'operator-profile-placeholder-image'}/>
                                                    : <LazyImage
                                                        src={tempImage[0] ?? imageUrl}
                                                        noImage={<DefaultUserImage className={'operator-profile-placeholder-image'}/>}
                                                        alt={'user profile'}
                                                    />}
                                        <div
                                            className={classnames('call-to-action', {'not-accepted': notAccepted})}>
                                            {notAccepted ? "This format is not supported for your profile image" : "Drag & Drop a New Picture"}
                                        </div>
                                    </div>
                                </>
                            )}
                        </NewDropZone>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Button
                            disabled={!(!!tempImage[0]) || !canSubmit}
                            onClick={updateProfileImageOfUser}
                            color={'primary'}
                            variant={'contained'}
                            size={'medium'}
                            sx={{minWidth: 200, marginInline: 'auto', display: 'flex'}}
                        >
                            Save
                        </Button>
                    </Grid2>
                </Grid2>
            </Card>
        </>
    );
}

export default EditOperatorProfileImageSection;
